import type { UIViewInjectedProps } from '@uirouter/react';
import { observer } from 'mobx-react-lite';
import { useEffect, useMemo, useState } from 'react';

import { api } from '@/api';
import type { WrappedItemsResult, WrappedResult } from '@/api/types';
import { DrawerForm } from '@/components/drawer-form';
import { boardingStore } from '@/stores';
import { getTranslatedString } from '@/utils';
import { showError } from '@/utils/common';

import { getFormFields } from './setup';

const entity = 'boardings';
const store = boardingStore;

export const LotsSelector = observer(({ transition }: UIViewInjectedProps) => {
  const { boardingId: itemId } = transition.router.globals.params;
  const foundItem = store.items.find(({ id }: any) => id === +itemId);
  const foundItemLotsIds = useMemo(() => foundItem?.lots.map(({ id }) => id) || [], [foundItem]);
  const contractProductId = +foundItem?.product.id;

  const [lots, setLots] = useState<cyrian.api.boardings.Lot[]>([]);
  const [targetLots, setTargetLots] = useState<number[]>(foundItemLotsIds);
  const [loading, setLoading] = useState<boolean>(false);
  const [warning, setWarning] = useState<boolean>(false);

  useEffect(() => {
    if (!foundItem) {
      return;
    }
    setLoading(true);
    api.boardings
      .getAvailableLots(itemId)
      .source.then(({ data }: WrappedItemsResult<cyrian.api.boardings.Lot[]>) => {
        setTargetLots(foundItemLotsIds);
        setLots([...data.items, ...(foundItem?.lots || [])]);
      })
      .then(() => setLoading(false));
  }, [foundItem, foundItemLotsIds, itemId]);

  const onClose = () => {
    transition.router.stateService.go(`base-layout.${entity}`);
  };

  const title = getTranslatedString('boardings.boarding-number', { 0: itemId });
  const formFields = getFormFields(contractProductId, lots, targetLots, warning, setTargetLots, setWarning);

  const onSubmit = (values: any) => {
    const { lots: finalLots } = values;
    const addDiff = finalLots.filter((lotId) => !foundItemLotsIds.includes(lotId));
    const removeDiff = foundItemLotsIds.filter((lotId) => !finalLots.includes(lotId));
    const promises = [];

    if (addDiff.length) {
      promises.push(
        api.boardings
          .addLots(itemId, { lotIds: addDiff })
          .source.then(({ data }: WrappedResult<cyrian.api.boardings.Boarding>) => store.updateItem(data))
          .catch(showError),
      );
    }

    if (removeDiff.length) {
      promises.push(
        api.boardings
          .removeLots(itemId, { lotIds: removeDiff })
          .source.then(({ data }: WrappedResult<cyrian.api.boardings.Boarding>) => store.updateItem(data))
          .catch(showError),
      );
    }

    return Promise.all(promises);
  };

  const resourceController = {
    create: onSubmit,
  };

  const layout = {
    labelCol: { span: 0 },
    wrapperCol: { span: 24 },
    tailLayout: { wrapperCol: { offset: 0, span: 24 } },
  };

  return (
    <DrawerForm
      {...layout}
      title={title}
      initValues={{}}
      formFields={formFields}
      onClose={onClose}
      resourceController={resourceController}
      loaderCondition={loading || !foundItem}
    />
  );
});
